import useWallet from "lib/wallets/useWallet";
import { useMemo } from "react";
import useSWR from "swr";

import { fetchV2Rebates } from "lib/indexer/client";
import { expandDecimals } from "lib/numbers";

export type RebateInfoItem = {
  factor: bigint;
  value: bigint;
  marketAddress: string;
  timeKey: string;
  tokenAddress: string;
  valueByFactor: bigint;
  id: string;
};

export type RebatesInfoResult = {
  accruedPositionPriceImpactFees: RebateInfoItem[];
  claimablePositionPriceImpactFees: RebateInfoItem[];
};

export function useRebatesInfoRequest(chainId: number, enabled: boolean): RebatesInfoResult {
  const { account } = useWallet();

  const key = enabled && chainId && account ? [chainId, "useRebatesInfo", account] : null;

  const { data } = useSWR<RebateInfoItem[]>(key, async () => {
    const res = await fetchV2Rebates({ account: account!.toLowerCase() });
    return res.items.map((raw) => {
      let factor = BigInt(raw.factor ?? 0);
      const factorByTime = BigInt(raw.factorByTime ?? 0);
      if (factorByTime > factor) factor = factorByTime;

      const value = BigInt(raw.value ?? 0);
      const valueByFactor = (value * factor) / expandDecimals(1, 30);

      return {
        factor,
        value,
        valueByFactor,
        timeKey: raw.timeKey,
        marketAddress: raw.marketAddress,
        tokenAddress: raw.tokenAddress,
        id: raw.id,
      };
    });
  });

  const { accruedPositionPriceImpactFees, claimablePositionPriceImpactFees } = useMemo(() => {
    const res: RebatesInfoResult = { accruedPositionPriceImpactFees: [], claimablePositionPriceImpactFees: [] };

    data?.forEach((rebateInfo) => {
      if (rebateInfo.factor > 0n && rebateInfo.valueByFactor === 0n) return;

      if (rebateInfo.factor === 0n) {
        res.accruedPositionPriceImpactFees.push(rebateInfo);
      } else {
        res.claimablePositionPriceImpactFees.push(rebateInfo);
      }
    });

    return res;
  }, [data]);

  return { accruedPositionPriceImpactFees, claimablePositionPriceImpactFees };
}
